import {
  Text,
  GreyGrey80,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import {
  dashboardv99990101Client,
  type Dashboardv99990101Api
} from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api'
import { useQuery } from '@sequencehq/api/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import { useCreditNoteQuery } from 'CreditNotes/hooks/useCreditNote'
import { match, P } from 'ts-pattern'
import type { ReactNode } from 'react'

type InvoiceStatus = Dashboardv99990101Api.Invoices.InvoiceStatus

type Props = {
  creditNoteId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const DeleteCreditNoteDialog = ({
  creditNoteId,
  isOpen,
  onClose,
  onSuccess
}: Props) => {
  const { displayNotification } = useNotifications()
  const { warning, isLoading } = useBillingCreatedWarning(creditNoteId)

  const deleteCreditNote = useMutation(
    dashboardv99990101Client.deleteCreditNote,
    {
      onSuccess: () => {
        displayNotification('Credit note deleted', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to delete credit note', { type: 'error' })
      }
    }
  )

  const handleSubmit = async () => {
    try {
      await deleteCreditNote.mutateAsync({ id: creditNoteId })
    } catch {
      return false
    }

    onSuccess?.()
    return true
  }

  return (
    <SimpleModal
      data-testid="modal.deleteCreditNote"
      variant="dialog"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Delete credit note"
      submitButtonText="Delete credit note"
      submittingButtonText="Deleting..."
      submitDisabled={isLoading}
      isDestructive
    >
      <Text
        {...Primary13Regular}
        color={GreyGrey80}
        data-testid="modal.deleteCreditNote.message"
      >
        {match(warning)
          .with({ type: 'NONE' }, () => (
            <>
              This will delete the credit note immediately. This action cannot
              be undone.
            </>
          ))
          .with({ type: 'BILLING' }, () => (
            <>
              This credit note was created by a billing schedule. If you delete
              it, your customer may be overcharged.
            </>
          ))
          .with({ type: 'BILLING_WITH_INVOICE' }, ({ invoice }) => (
            <>
              This credit note was created by a billing schedule to correct{' '}
              {match(invoice)
                .with({ status: 'IN_PROGRESS' }, () => (
                  <>
                    an{' '}
                    <InvoiceLink invoiceId={invoice.id}>
                      upcoming invoice
                    </InvoiceLink>
                  </>
                ))
                .with({ status: 'DRAFT' }, { number: undefined }, () => (
                  <>
                    a{' '}
                    <InvoiceLink invoiceId={invoice.id}>
                      draft invoice
                    </InvoiceLink>
                  </>
                ))
                .with({ number: P.string }, ({ number }) => (
                  <>
                    invoice{' '}
                    <InvoiceLink invoiceId={invoice.id}>{number}</InvoiceLink>
                  </>
                ))
                .exhaustive()}
              . If you delete it, your customer may be overcharged.
            </>
          ))
          .exhaustive()}
      </Text>
    </SimpleModal>
  )
}

const InvoiceLink = ({
  invoiceId,
  children
}: {
  invoiceId: string
  children: ReactNode
}) => (
  <Link
    to={`/invoices/${invoiceId}`}
    color={IndigoIndigo60}
    {...Primary13SemiBold}
    data-testid="modal.deleteCreditNote.invoiceLink"
  >
    {children}
  </Link>
)

type BillingCreatedWarning =
  | { type: 'NONE' }
  | { type: 'BILLING' }
  | {
      type: 'BILLING_WITH_INVOICE'
      // Draft and upcoming invoices have no number yet.
      invoice: { id: string; number: string | undefined; status: InvoiceStatus }
    }

/**
 * Legacy billing linked credit notes to a placeholder invoice from their own
 * run. Only an invoice from a different run is the one being corrected.
 *
 *   credit note run == invoice run  -> placeholder, no link
 *   credit note run != invoice run  -> corrected invoice, link it
 */
const useBillingCreatedWarning = (
  creditNoteId: string
): { warning: BillingCreatedWarning; isLoading: boolean } => {
  const creditNoteQuery = useCreditNoteQuery(creditNoteId)
  const billingRunId = creditNoteQuery.data?.billingRunId
  const invoiceId = creditNoteQuery.data?.invoiceId
  const shouldFetchInvoice = Boolean(billingRunId && invoiceId)

  // A missing invoice (e.g. a deleted placeholder) is expected, so don't retry.
  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: invoiceId ?? '' },
    { enabled: shouldFetchInvoice, retry: false, staleTime: 1 * MINUTES }
  )
  const invoice = invoiceQuery.data

  // Without the credit note we can't tell if billing made it, so keep delete blocked.
  const isLoading =
    !creditNoteQuery.data || (shouldFetchInvoice && invoiceQuery.isPending)

  if (!billingRunId) {
    return { warning: { type: 'NONE' }, isLoading }
  }

  if (invoice && invoice.billingRunId !== billingRunId) {
    return {
      warning: {
        type: 'BILLING_WITH_INVOICE',
        invoice: {
          id: invoice.id,
          number: invoice.invoiceNumber,
          status: invoice.status
        }
      },
      isLoading
    }
  }

  return { warning: { type: 'BILLING' }, isLoading }
}
